// Free gift orchestrator.
//
// All eligibility math lives server-side in cart-products-alternate.liquid,
// which reads the shop metafields custom.free_gift_rules and
// custom.free_gift_products (projected by the back office) and renders one
// hidden .tb-free-gift-state span per resolvable rule on every cart morph,
// plus one .tb-free-gift-orphans span listing gift line keys that belong to no
// projected rule. This module only reads those spans and issues the matching
// cart mutation: add the gift when a rule is eligible, remove it when the rule
// stopped being eligible or left the payload, repair the quantity back to one.
// It never computes collection membership, totals, or date windows itself,
// never opens the drawer, and never calls any external host. Pricing is owned
// elsewhere (a checkout cart transform function zeroes eligible gift lines);
// this module only writes the _priority_free intent marker and can never make
// a line free by itself.
//
// Re-entrance: a single in-flight flag plus a debounce absorb cart:update
// bursts (discount flows re-render twice). Each pass issues at most one
// mutation; that mutation's own cart:update (source "free-gift") schedules the
// next debounced pass, so multi-step convergence (several rules, several
// orphans) settles over successive passes against fresh server-rendered state.

const SOURCE = 'free-gift';
const DEBOUNCE_MS = 200;
const DISMISSED_PREFIX = 'tb-free-gift-dismissed:';
const ADDED_PREFIX = 'tb-free-gift-added:';

if (!window.__tbFreeGift) {
  window.__tbFreeGift = true;

  // Same id the drawer's cart-items-component registers for the Section
  // Rendering API. Resolved from the DOM so a section rename never desyncs us.
  const sectionId = () => {
    const component = document.querySelector('cart-items-component[data-drawer]');
    return (component && component.dataset.sectionId) || 'cart_drawer';
  };

  let inFlight = false;
  let debounceTimer = null;
  // Rules whose add failed this page load (gift out of stock, cart validation).
  // Never retried until the next page load.
  const failedRules = new Set();

  const session = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        /* private mode: degrade to no memory */
      }
    },
    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        /* noop */
      }
    },
  };

  const readState = () => {
    const state = [];
    for (const span of document.querySelectorAll('.tb-free-gift-state')) {
      state.push({
        ruleId: span.dataset.ruleId || '',
        eligible: span.dataset.eligible === 'true',
        variantId: parseInt(span.dataset.variantId, 10) || 0,
        available: span.dataset.available === 'true',
        giftKey: span.dataset.giftKey || '',
        giftQty: parseInt(span.dataset.giftQty, 10) || 0,
      });
    }
    return state;
  };

  const readOrphanKeys = () => {
    const span = document.querySelector('.tb-free-gift-orphans');
    if (!span || !span.dataset.orphanGiftKeys) return [];
    return span.dataset.orphanGiftKeys.split(',').filter(Boolean);
  };

  const dispatchCartUpdate = (cartJson, sections) => {
    let itemCount = 0;
    const sectionHtml = sections && sections[sectionId()];
    if (sectionHtml) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const countRef = doc.querySelector('[ref="cartItemCount"]');
      itemCount = parseInt(countRef && countRef.textContent, 10) || 0;
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartJson,
          sourceId: SOURCE,
          data: {
            source: SOURCE,
            itemCount,
            sections,
          },
        },
      })
    );
  };

  const mutateCart = async (path, body) => {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: sectionId(),
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) {
      throw new Error(`free-gift cart mutation failed (${response.status})`);
    }
    const json = await response.json();
    dispatchCartUpdate(json, json.sections);
  };

  const addGift = (rule) =>
    mutateCart('/cart/add.js', {
      items: [
        {
          id: rule.variantId,
          quantity: 1,
          properties: { _priority_free: 'true' },
        },
      ],
    });

  const removeGift = (giftKey) => mutateCart('/cart/change.js', { id: giftKey, quantity: 0 });

  const repairGiftQuantity = (giftKey) => mutateCart('/cart/change.js', { id: giftKey, quantity: 1 });

  const evaluate = async () => {
    if (inFlight) return;

    // Orphans first: gift lines whose rule left the payload render as normal
    // paid rows, so they must go before any add. One mutation per pass; the
    // resulting cart:update schedules the next pass for the remaining ones.
    const orphanKeys = readOrphanKeys();
    if (orphanKeys.length > 0) {
      inFlight = true;
      try {
        await removeGift(orphanKeys[0]);
      } catch (error) {
        console.error('[free-gift]', error);
      } finally {
        inFlight = false;
      }
      return;
    }

    const state = readState();

    // A gift line claimed by an eligible rule must never be removed on behalf
    // of an ineligible rule that shares the same gift product.
    const claimedKeys = new Set();
    for (const rule of state) {
      if (rule.eligible && rule.giftKey !== '') claimedKeys.add(rule.giftKey);
    }

    for (const rule of state) {
      const hasGift = rule.giftKey !== '';
      const dismissed = session.get(DISMISSED_PREFIX + rule.ruleId) === '1';
      const wasAdded = session.get(ADDED_PREFIX + rule.ruleId) === '1';

      try {
        if (rule.eligible && !hasGift) {
          if (wasAdded && !dismissed) {
            // We added it earlier and it vanished while still eligible: the
            // customer removed it on purpose. Respect that for the session,
            // for every rule offering the same gift product (any of them
            // would otherwise re-add it straight away).
            for (const other of state) {
              if (other.variantId === rule.variantId) {
                session.set(DISMISSED_PREFIX + other.ruleId, '1');
              }
            }
            continue;
          }
          if (dismissed || failedRules.has(rule.ruleId) || !rule.available) continue;
          inFlight = true;
          await addGift(rule);
          session.set(ADDED_PREFIX + rule.ruleId, '1');
          return;
        }

        if (rule.eligible && hasGift && rule.giftQty > 1) {
          // The gift is locked at quantity one; repair a crafted or replayed
          // bump (the checkout function refuses to zero quantity above one).
          inFlight = true;
          await repairGiftQuantity(rule.giftKey);
          return;
        }

        if (!rule.eligible && hasGift && !claimedKeys.has(rule.giftKey)) {
          inFlight = true;
          await removeGift(rule.giftKey);
          session.remove(ADDED_PREFIX + rule.ruleId);
          return;
        }

        if (!rule.eligible) {
          // Conditions dropped: forget history so the gift comes back if the
          // customer requalifies later in the session.
          session.remove(ADDED_PREFIX + rule.ruleId);
          session.remove(DISMISSED_PREFIX + rule.ruleId);
        }
      } catch (error) {
        failedRules.add(rule.ruleId);
        console.error('[free-gift]', error);
        return;
      } finally {
        if (inFlight) {
          inFlight = false;
          // The mutation dispatched its own cart:update; the debounced
          // listener below re-evaluates once against fresh state.
        }
      }
    }
  };

  const scheduleEvaluate = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, DEBOUNCE_MS);
  };

  document.addEventListener('cart:update', scheduleEvaluate);
  document.addEventListener('discount:update', scheduleEvaluate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleEvaluate());
  } else {
    scheduleEvaluate();
  }
}
